import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  User,
  Users,
  Ticket,
  Check,
  XCircle,
  Mail,
  Phone,
  X,
  Maximize2,
  CalendarDays,
  Sparkles,
  CircleDot,
  Share2,
  UserPlus,
} from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";
import ConferenceCard from "../../components/event_media/ConferenceCard";

export default function EventDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  useEffect(() => {
    if (!slug) {
      toast.error("Event not found");
      navigate("/event-media-sales");
      return;
    }
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await conferenceAndTradeMissionService.getEventDetails(slug);
        const event = response.event ?? response.data?.event ?? response.data;
        const similar_event = response.similar_event ?? response.data?.similar_event ?? [];
        if (response.status && event) {
          setEventData({
            event,
            similar_event: Array.isArray(similar_event) ? similar_event : [],
          });
        } else {
          toast.error(response.message || "Failed to load event");
          navigate("/event-media-sales");
        }
      } catch (error) {
        toast.error(error?.message || "An error occurred");
        navigate("/event-media-sales");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [slug, navigate]);

  useEffect(() => {
    if (isImageModalOpen) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e) => {
        if (e.key === "Escape") setIsImageModalOpen(false);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    }
    document.body.style.overflow = "unset";
  }, [isImageModalOpen]);

  const handleShareWithClient = () => {
    const fallbackUrl = `https://tripsecuregh.com/event-details/${slug}`;
    const url = window.location.hostname.includes("localhost")
      ? `${window.location.origin}/event-details/${slug}`
      : fallbackUrl;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
      toast.success("Registration link copied. Share with your client.");
    } else {
      window.open(url, "_blank");
      toast.info("Open the link and share with your client.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="flex flex-col gap-4 items-center">
          <div className="w-12 h-12 rounded-full border-2 border-emerald-200 border-t-emerald-500 animate-spin" />
          <p className="text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!eventData?.event) return null;

  const event = eventData.event;
  const similarEvents = eventData.similar_event || [];
  const eventTypes = event.event_type || [];
  const hasMultipleTypes = eventTypes.length > 1;
  const isConference = eventTypes.some((t) => t?.toLowerCase().includes("conference"));
  const isTradeMission = eventTypes.some(
    (t) => t?.toLowerCase().includes("trade") || t?.toLowerCase().includes("mission")
  );

  const formatEventTypeName = (type) => {
    if (!type) return "Event";
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getEventTypeColor = (type) => {
    const n = type?.toLowerCase() || "";
    if (n.includes("conference")) return "bg-gradient-to-r from-blue-500 to-blue-600";
    if (n.includes("trade") || n.includes("mission")) return "bg-gradient-to-r from-purple-500 to-purple-600";
    if (n.includes("workshop") || n.includes("seminar")) return "bg-gradient-to-r from-orange-500 to-orange-600";
    if (n.includes("exhibition") || n.includes("expo")) return "bg-gradient-to-r from-pink-500 to-pink-600";
    if (n.includes("festival") || n.includes("celebration")) return "bg-gradient-to-r from-yellow-500 to-yellow-600";
    if (n.includes("summit") || n.includes("forum")) return "bg-gradient-to-r from-indigo-500 to-indigo-600";
    return "bg-gradient-to-r from-emerald-500 to-emerald-600";
  };

  const getEventTypeBadgeColor = (type) => {
    const n = type?.toLowerCase() || "";
    if (n.includes("conference")) return "text-blue-700 bg-blue-50 border-blue-200";
    if (n.includes("trade") || n.includes("mission")) return "text-purple-700 bg-purple-50 border-purple-200";
    if (n.includes("workshop") || n.includes("seminar")) return "text-orange-700 bg-orange-50 border-orange-200";
    if (n.includes("exhibition") || n.includes("expo")) return "text-pink-700 bg-pink-50 border-pink-200";
    if (n.includes("festival") || n.includes("celebration")) return "text-yellow-700 bg-yellow-50 border-yellow-200";
    if (n.includes("summit") || n.includes("forum")) return "text-indigo-700 bg-indigo-50 border-indigo-200";
    return "text-emerald-700 bg-emerald-50 border-emerald-200";
  };

  const getMapIframeUrl = () => {
    if (!event.venue_google_location) {
      const address = `${event.venue_street_address || ""}, ${event.city || ""}, ${event.country || ""}`
        .trim()
        .replace(/^,\s*|,\s*$/g, "");
      if (!address) return null;
      return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
    }
    if (event.venue_google_location.includes("maps/embed")) return event.venue_google_location;
    const address = `${event.venue_street_address || ""}, ${event.city || ""}, ${event.country || ""}`
      .trim()
      .replace(/^,\s*|,\s*$/g, "");
    if (!address) return null;
    return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [h, m] = timeString.split(":");
      const h12 = parseInt(h) % 12 || 12;
      const ampm = parseInt(h) >= 12 ? "PM" : "AM";
      return `${h12}:${m} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const formatCurrency = (currency) => {
    if (!currency) return "₵";
    const u = currency.toUpperCase();
    if (u === "USD") return "$";
    if (u === "GHS") return "₵";
    return currency;
  };

  const allImages = event.featured_images?.length
    ? [event.thumbnail, ...event.featured_images].filter(Boolean)
    : [event.thumbnail].filter(Boolean);
  const eventStartDate = event.event_start_date || event.event_date;
  const eventEndDate = event.event_end_date || event.event_date;
  const eventDate =
    eventStartDate && eventEndDate && eventStartDate !== eventEndDate
      ? `${formatDate(eventStartDate)} – ${formatDate(eventEndDate)}`
      : formatDate(eventStartDate || event.event_date);
  const eventTime = formatTime(event.event_time);
  const isEventClosed = event.event_state === "closed";
  const mapEmbedUrl = getMapIframeUrl();
  const overallSlots = event.overall_slots ?? null;

  const minPrice =
    isConference && event.ticket_types?.length
      ? Math.min(...event.ticket_types.map((t) => t.price || 0))
      : null;
  const minPriceTicket =
    isConference && event.ticket_types?.length
      ? event.ticket_types.find((t) => t.price === minPrice)
      : null;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);

  const backToList = () => {
    if (isConference || isTradeMission) navigate("/event-media-sales/conference-trade-mission");
    else navigate("/event-media-sales/events");
  };

  const organizerDisplay =
    typeof event.organizer === "object"
      ? event.organizer?.name || "Event Organizer"
      : typeof event.organizer === "string"
        ? event.organizer
        : event.organizer_name?.name ?? event.organizer_name ?? event.organizer_slug ?? "Event Organizer";

  const scheduleData = event.agenda?.length > 0 ? event.agenda : event.itinerary?.length > 0 ? event.itinerary : null;

  return (
    <div className="bg-gradient-to-b from-gray-50 via-white to-gray-50 min-h-screen">
      <div className="px-4 pt-24 pb-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <motion.button
            onClick={backToList}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -4, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex gap-2 items-center mb-6 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-lg border-2 border-gray-300 shadow-md transition-all hover:bg-gray-50 hover:border-emerald-400 hover:shadow-lg"
          >
            <ChevronLeft size={18} />
            Back to {isConference || isTradeMission ? "Conference & Trade Mission" : "Events"}
          </motion.button>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            <div className="space-y-12 lg:col-span-2">
              {/* Event Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="overflow-hidden relative rounded-2xl shadow-xl"
              >
                <div className="overflow-hidden relative h-64 bg-gradient-to-br from-emerald-100 via-blue-100 to-purple-100 sm:h-80 lg:h-[500px]">
                  <div className="absolute inset-0 z-10 bg-gradient-to-t to-transparent from-black/60 via-black/20" />
                  {allImages.length > 0 && (
                    <>
                      <AnimatePresence mode="wait">
                        <motion.img
                          key={currentImageIndex}
                          src={allImages[currentImageIndex]}
                          alt={event.event_title || "Event"}
                          initial={{ opacity: 0, scale: 1.1 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.5 }}
                          className="object-cover w-full h-full cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => setIsImageModalOpen(true)}
                        />
                      </AnimatePresence>
                      <motion.button
                        onClick={() => setIsImageModalOpen(true)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="flex absolute right-4 bottom-4 z-20 gap-2 items-center px-4 py-2 text-sm font-semibold text-white rounded-lg border backdrop-blur-md bg-black/50 border-white/20 hover:bg-black/70"
                      >
                        <Maximize2 size={18} />
                        <span className="hidden sm:inline">View Full Image</span>
                      </motion.button>
                      {allImages.length > 1 && (
                        <>
                          <motion.button
                            onClick={prevImage}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="flex absolute left-4 top-1/2 z-20 justify-center items-center w-12 h-12 text-white rounded-full shadow-lg backdrop-blur-md bg-black/40 hover:bg-black/60 -translate-y-1/2"
                          >
                            <ChevronLeft size={22} />
                          </motion.button>
                          <motion.button
                            onClick={nextImage}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="flex absolute right-4 top-1/2 z-20 justify-center items-center w-12 h-12 text-white rounded-full shadow-lg backdrop-blur-md bg-black/40 hover:bg-black/60 -translate-y-1/2"
                          >
                            <ChevronRight size={22} />
                          </motion.button>
                          <div className="flex absolute bottom-4 left-1/2 gap-2 -translate-x-1/2 z-20">
                            {allImages.map((_, i) => (
                              <button
                                key={i}
                                onClick={() => setCurrentImageIndex(i)}
                                className={`h-2 rounded-full transition-all ${
                                  currentImageIndex === i ? "w-8 bg-white" : "w-2 bg-white/50 hover:bg-white/75"
                                }`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                      <div className="absolute top-4 left-4 z-20 flex flex-wrap gap-2">
                        {eventTypes.length > 0 ? (
                          eventTypes.slice(0, hasMultipleTypes ? 2 : 1).map((type, i) => (
                            <motion.span
                              key={`${type}-${i}`}
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.2 + i * 0.1 }}
                              className={`inline-flex px-4 py-1.5 text-xs font-bold text-white rounded-full backdrop-blur-md shadow-lg ${getEventTypeColor(type)}`}
                            >
                              {formatEventTypeName(type)}
                            </motion.span>
                          ))
                        ) : (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="inline-flex px-4 py-1.5 text-xs font-bold text-white rounded-full backdrop-blur-md shadow-lg bg-gradient-to-r from-gray-500 to-gray-600"
                          >
                            Event
                          </motion.span>
                        )}
                        {hasMultipleTypes && eventTypes.length > 2 && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.4 }}
                            className="inline-flex px-4 py-1.5 text-xs font-bold text-white rounded-full backdrop-blur-md shadow-lg bg-gradient-to-r from-gray-600 to-gray-700"
                            title={eventTypes.slice(2).map(formatEventTypeName).join(", ")}
                          >
                            +{eventTypes.length - 2}
                          </motion.span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="relative z-10 p-6 bg-white sm:p-8 lg:p-10">
                  <div className="flex flex-wrap gap-3 items-center mb-4">
                    {eventTypes.length > 0
                      ? eventTypes.map((type, i) => (
                          <motion.span
                            key={`${type}-${i}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + i * 0.1 }}
                            className={`px-4 py-1.5 text-xs font-bold rounded-full border ${getEventTypeBadgeColor(type)}`}
                          >
                            {formatEventTypeName(type)}
                          </motion.span>
                        ))
                      : (
                        <motion.span
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="px-4 py-1.5 text-xs font-bold text-gray-700 bg-gray-50 border border-gray-200 rounded-full"
                        >
                          Event
                        </motion.span>
                      )}
                    {isConference && minPrice != null && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="px-4 py-1.5 text-lg font-bold bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg"
                      >
                        From {formatCurrency(minPriceTicket?.currency)}
                        {minPrice}
                      </motion.span>
                    )}
                  </div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-6 text-2xl font-extrabold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 sm:text-3xl lg:text-4xl"
                  >
                    {event.event_title || "Untitled Event"}
                  </motion.h1>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex flex-wrap gap-6 mb-8 text-sm"
                  >
                    <div className="flex gap-2 items-center px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-100">
                      <Calendar className="text-emerald-600" size={18} />
                      <span className="font-semibold text-gray-900">{eventDate}</span>
                      <span className="text-gray-400">•</span>
                      <Clock className="text-emerald-600" size={18} />
                      <span className="text-gray-700">{eventTime}</span>
                    </div>
                    <div className="flex gap-2 items-center px-4 py-2 bg-blue-50 rounded-lg border border-blue-100">
                      <MapPin className="text-blue-600" size={18} />
                      <span className="font-semibold text-gray-900">{event.venue_name || "Venue TBA"}</span>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-wrap gap-4 items-center"
                  >
                    {isEventClosed ? (
                      <div className="flex gap-3 items-center px-6 py-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl border-2 border-gray-300 shadow-md">
                        <XCircle size={24} className="text-gray-500" />
                        <div>
                          <p className="text-base font-bold text-gray-700">Event Closed</p>
                          <p className="text-sm text-gray-600">Registration for this event has been closed</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <motion.button
                          onClick={handleShareWithClient}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-8 py-4 text-base font-bold text-white rounded-xl shadow-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                        >
                          <span className="inline-flex gap-2 items-center">
                            <Share2 size={20} />
                            Share with client
                          </span>
                        </motion.button>
                        {(isConference || isTradeMission) ? (
                          <motion.button
                            onClick={() => navigate(`/event-media-sales/event-details/${slug}/register`)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-8 py-4 text-base font-bold text-white rounded-xl shadow-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                          >
                            <span className="inline-flex gap-2 items-center">
                              <UserPlus size={20} />
                              Register (for client)
                            </span>
                          </motion.button>
                        ) : (
                          <motion.button
                            onClick={() => navigate(`/event-media-sales/event-details/${slug}/book`)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-8 py-4 text-base font-bold text-white rounded-xl shadow-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                          >
                            <span className="inline-flex gap-2 items-center">
                              <Ticket size={20} />
                              Buy Ticket
                            </span>
                          </motion.button>
                        )}
                        {overallSlots != null && (
                          <div className="flex gap-2 items-center px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                            <Users size={16} className="text-emerald-600" />
                            <span className="text-sm font-medium text-gray-700">
                              {overallSlots} {isConference ? "seats" : "slots"} available
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </motion.div>
                </div>
              </motion.div>

              {/* About */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div className="absolute top-0 bottom-0 -left-4 w-1 bg-gradient-to-b from-emerald-400 to-emerald-600 rounded-full" />
                <h2 className="mb-6 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                  About the Event
                </h2>
                <p className="text-lg leading-relaxed text-gray-700 whitespace-pre-line">
                  {event.about_event || "No description available."}
                </p>
              </motion.div>

              {/* Ticket Types - Conferences only */}
              {isConference && event.ticket_types?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                    Ticket Options
                  </h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {event.ticket_types.map((ticket, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -4 }}
                        className="relative p-8 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 hover:border-emerald-400 hover:shadow-xl transition-all"
                      >
                        <div className="absolute top-4 right-4">
                          <Ticket size={28} className="text-emerald-500 opacity-30" />
                        </div>
                        <h3 className="mb-3 text-xl font-bold text-gray-900">{ticket.name}</h3>
                        <div className="mb-6">
                          <span className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500">
                            {formatCurrency(ticket.currency)}
                            {ticket.price}
                          </span>
                        </div>
                        {ticket.description && (
                          <p className="mb-4 text-sm leading-relaxed text-gray-600">{ticket.description}</p>
                        )}
                        {ticket.slots_allocated != null && (
                          <div className="flex gap-2 items-center mb-4 text-sm text-gray-600">
                            <Users size={16} className="text-emerald-600" />
                            <span>{ticket.slots_allocated} slot{ticket.slots_allocated !== 1 ? "s" : ""} allocated</span>
                          </div>
                        )}
                        {ticket.whats_included?.length > 0 && (
                          <div className="pt-4 mb-4 border-t border-gray-200">
                            <p className="mb-3 text-xs font-semibold tracking-wide text-gray-700 uppercase">
                              What&apos;s Included
                            </p>
                            <ul className="space-y-2">
                              {ticket.whats_included.map((item, idx) => (
                                <li key={idx} className="flex gap-2 items-start text-sm text-gray-600">
                                  <Check size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Performers / Speakers */}
              {event.performers?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                    {isConference || isTradeMission ? "Speakers" : "Performers / Entertainers"}
                  </h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {event.performers.map((speaker, index) => {
                      const name = typeof speaker === "string" ? speaker : speaker?.name;
                      const image = typeof speaker === "object" && speaker?.image ? speaker.image : null;
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 4 }}
                          className="flex gap-4 items-center p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-100 hover:shadow-md transition-all"
                        >
                          {image ? (
                            <img src={image} alt={name} className="flex-shrink-0 w-14 h-14 rounded-full object-cover shadow-lg" />
                          ) : (
                            <div className="flex flex-shrink-0 justify-center items-center w-14 h-14 font-bold text-white bg-gradient-to-br from-emerald-500 via-emerald-600 to-blue-600 rounded-full shadow-lg">
                              <User size={24} />
                            </div>
                          )}
                          <p className="text-lg font-semibold text-gray-900">{name}</p>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Sponsors */}
              {event.sponsors?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                    Sponsors/Partners
                  </h2>
                  <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-4">
                    {event.sponsors.map((sponsor, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.1, y: -4 }}
                        className="flex justify-center items-center p-6 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all"
                      >
                        <img
                          src={sponsor.logo}
                          alt={sponsor.name}
                          className="object-contain max-w-full max-h-20 sm:max-h-24 lg:max-h-28"
                        />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Organizer */}
              {(event.organizer || event.organizer_name || event.organizer_slug) && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
                >
                  <h3 className="mb-6 text-lg font-bold text-gray-900">Organizer</h3>
                  <div className="mb-6 space-y-4">
                    <div className="flex gap-4 items-start">
                      {typeof event.organizer === "object" && event.organizer?.logo ? (
                        <img
                          src={event.organizer.logo}
                          alt={event.organizer.name || "Organizer"}
                          className="object-cover w-16 h-16 rounded-xl border-2 border-gray-200 shadow-md flex-shrink-0"
                        />
                      ) : (
                        <div className="flex flex-shrink-0 justify-center items-center w-16 h-16 text-xl font-bold text-white bg-emerald-600 rounded-xl shadow-lg">
                          {organizerDisplay.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-lg font-bold text-gray-900">{organizerDisplay}</h4>
                        {typeof event.organizer === "object" && event.organizer?.city && event.organizer?.country && (
                          <div className="flex gap-1 items-center text-sm text-gray-600">
                            <MapPin size={14} className="text-emerald-600" />
                            <span>{event.organizer.city}, {event.organizer.country}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="pt-4 space-y-3 border-t border-gray-200">
                      <motion.a
                        href="mailto:info@tripsecuregh.com?subject=Inquiry about Event"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex gap-3 justify-center items-center px-4 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg shadow-sm hover:bg-emerald-700 transition-all"
                      >
                        <Mail size={18} />
                        <span>Email Event Team</span>
                      </motion.a>
                      <motion.a
                        href="tel:+233204014370"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex gap-3 justify-center items-center px-4 py-3 text-sm font-medium text-emerald-700 bg-white rounded-lg border-2 border-emerald-600 hover:bg-emerald-50 transition-all"
                      >
                        <Phone size={18} />
                        <span>Call Event Team</span>
                      </motion.a>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Venue */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
              >
                <h3 className="mb-6 text-lg font-bold text-gray-900">Venue</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="mb-2 font-bold text-gray-900">{event.venue_name || "Venue TBA"}</p>
                    <p className="text-sm text-gray-600">
                      {event.venue_street_address}
                      {event.city && `, ${event.city}`}
                      {event.country && `, ${event.country}`}
                    </p>
                  </div>
                  {mapEmbedUrl && (
                    <div className="overflow-hidden rounded-xl border-2 border-gray-200 shadow-md">
                      <iframe
                        src={mapEmbedUrl}
                        width="100%"
                        height="220"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Map of ${event.venue_name}`}
                        className="w-full"
                      />
                    </div>
                  )}
                  {event.venue_google_location && (
                    <motion.a
                      href={event.venue_google_location}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md hover:from-orange-600 hover:to-orange-700"
                    >
                      <ExternalLink size={16} />
                      Get directions
                    </motion.a>
                  )}
                </div>
              </motion.div>

              {/* Event Schedule */}
              {scheduleData && scheduleData.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="overflow-hidden p-6 bg-gradient-to-br from-white to-white rounded-2xl border border-emerald-200 shadow-lg via-emerald-50/30"
                >
                  <div className="flex gap-3 items-center mb-6">
                    <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-md">
                      <CalendarDays size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Event Schedule</h3>
                      <p className="text-xs font-medium text-emerald-600">
                        {scheduleData.length} {scheduleData.length === 1 ? "activity" : "activities"} planned
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="absolute left-[23px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-300 via-emerald-400 to-emerald-300" />
                    <div className="space-y-6">
                      {scheduleData.map((item, index) => {
                        const isFirst = index === 0;
                        const isLast = index === scheduleData.length - 1;
                        return (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="relative"
                          >
                            <div className="flex gap-4 items-start">
                              <div className="relative flex-shrink-0 z-10">
                                <div className="flex justify-center items-center w-12 h-12 bg-white rounded-full border-4 border-emerald-500 shadow-lg">
                                  {isFirst ? (
                                    <Sparkles size={18} className="text-emerald-600" />
                                  ) : (
                                    <CircleDot size={18} className="text-emerald-600" />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all">
                                <div className="flex gap-2 items-center mb-3">
                                  <div className="inline-flex gap-2 items-center px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg shadow-sm">
                                    <Clock size={14} className="text-white" />
                                    <span className="text-xs font-bold text-white">{item.time}</span>
                                  </div>
                                  {isFirst && (
                                    <span className="inline-flex px-2 py-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-md">START</span>
                                  )}
                                  {isLast && (
                                    <span className="inline-flex px-2 py-1 text-[10px] font-bold text-orange-700 bg-orange-100 rounded-md">END</span>
                                  )}
                                </div>
                                <h4 className="mb-2 text-base font-bold text-gray-900">{item.title}</h4>
                                {item.description && (
                                  <p className="text-sm leading-relaxed text-gray-600">{item.description}</p>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center pt-4 mt-6 border-t border-emerald-100">
                    <div className="flex justify-center items-center w-6 h-6 bg-emerald-100 rounded-full">
                      <Sparkles size={12} className="text-emerald-600" />
                    </div>
                    <p className="text-xs italic text-gray-600">Schedule may be subject to change</p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Similar Events */}
          {similarEvents.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="pt-12 mt-16 border-t-2 border-gray-200"
            >
              <h3 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                Similar Events You May Like
              </h3>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {similarEvents.slice(0, 4).map((similarEvent, index) => (
                  <motion.div
                    key={similarEvent.slug || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                  >
                    <ConferenceCard
                      item={similarEvent}
                      onClick={() => navigate(`/event-media-sales/event-details/${similarEvent.slug}`)}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Full Screen Image Modal */}
      <AnimatePresence>
        {isImageModalOpen && allImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/95"
            onClick={() => setIsImageModalOpen(false)}
          >
            <motion.button
              onClick={() => setIsImageModalOpen(false)}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              className="flex absolute top-4 right-4 justify-center items-center w-12 h-12 text-white rounded-full border backdrop-blur-md z-[60] bg-black/50 border-white/20 hover:bg-black/70"
            >
              <X size={24} />
            </motion.button>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={allImages[currentImageIndex]}
                alt={event.event_title || "Event"}
                className="max-w-full max-h-[95vh] object-contain rounded-lg shadow-2xl"
              />
              {allImages.length > 1 && (
                <>
                  <motion.button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="flex absolute left-4 top-1/2 justify-center items-center w-14 h-14 text-white rounded-full border backdrop-blur-md bg-black/50 border-white/20 hover:bg-black/70 -translate-y-1/2"
                  >
                    <ChevronLeft size={28} />
                  </motion.button>
                  <motion.button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="flex absolute right-4 top-1/2 justify-center items-center w-14 h-14 text-white rounded-full border backdrop-blur-md bg-black/50 border-white/20 hover:bg-black/70 -translate-y-1/2"
                  >
                    <ChevronRight size={28} />
                  </motion.button>
                  <div className="absolute bottom-4 left-1/2 px-4 py-2 text-sm font-semibold text-white rounded-full border backdrop-blur-md -translate-x-1/2 bg-black/50 border-white/20">
                    {currentImageIndex + 1} / {allImages.length}
                  </div>
                  <div className="flex absolute bottom-16 left-1/2 gap-2 -translate-x-1/2">
                    {allImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i); }}
                        className={`h-2 rounded-full transition-all ${
                          currentImageIndex === i ? "w-8 bg-white" : "w-2 bg-white/50 hover:bg-white/75"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthenticatedFooter />
    </div>
  );
}
